import { useState, FormEvent } from 'react'
import { apiFetch } from '@/lib/apiFetch'

interface SpellingListEditorProps {
  childId: number
  hasActiveList: boolean
  onSaved: () => void
  onCancel: () => void
}

export default function SpellingListEditor({ childId, hasActiveList, onSaved, onCancel }: SpellingListEditorProps) {
  const [name, setName] = useState('')
  const [wordsText, setWordsText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const wordCount = wordsText
    .split('\n')
    .map(w => w.trim())
    .filter(w => w.length > 0).length

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    const words = wordsText
      .split('\n')
      .map(w => w.trim())
      .filter(w => w.length > 0)
      .map(text => ({ text }))

    if (words.length === 0) {
      setError('Add at least one word.')
      return
    }

    setSubmitting(true)
    try {
      await apiFetch('/spelling/lists', {
        method: 'POST',
        body: JSON.stringify({
          childId,
          name: name || `Week of ${new Date().toLocaleDateString()}`,
          words,
        }),
      })
      onSaved()
    } catch (err: any) {
      setError(err.message || 'Failed to save list.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 border border-border rounded-lg">
      <div>
        <label htmlFor="listName" className="block text-sm font-medium mb-1">
          List Name
        </label>
        <input
          id="listName"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={`Week of ${new Date().toLocaleDateString()}`}
          className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
        />
      </div>

      <div>
        <label htmlFor="words" className="block text-sm font-medium mb-1">
          Spelling Words <span className="text-muted-foreground font-normal">(one per line)</span>
        </label>
        <textarea
          id="words"
          rows={8}
          value={wordsText}
          onChange={(e) => setWordsText(e.target.value)}
          placeholder={"apple\nbanana\nchocolate\ndifficult\nenvironment"}
          className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm font-mono"
        />
        <p className="text-xs text-muted-foreground mt-1">
          {wordCount} {wordCount === 1 ? 'word' : 'words'}
        </p>
      </div>

      {hasActiveList && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm">
          The current active list will be archived when you save a new one.
        </div>
      )}

      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50 min-h-[44px]"
        >
          {submitting ? 'Saving...' : 'Save List'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-border rounded-lg hover:bg-accent transition min-h-[44px]"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
